import { crmContactApi } from 'app/services/api';

import {
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  PENDING_CONTACT_LINK_INTENT,
} from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectBorrowerPendingContactLinkById } from '../selectors';

import { resolveBorrowerContactSummary } from './loanApplicationBorrowerCrmContacts.utils';

export const LOAN_APPLICATION_BORROWER_CRM_CONTACTS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/borrowerCrmContacts`,
  {
    SET_BORROWER_CRM_CONTACTS: 'SET_BORROWER_CRM_CONTACTS',
    SET_BORROWER_CRM_CONTACT: 'SET_BORROWER_CRM_CONTACT',
    RESET_BORROWER_CRM_CONTACTS: 'RESET_BORROWER_CRM_CONTACTS',
  },
);

// Resolve the linked CRM contact for every borrower on tab mount and store a
// borrowerId -> contactSummary map. Per-borrower failures are isolated inside
// resolveBorrowerContactSummary (return null), so Promise.all never rejects.
export const getBorrowerCrmContactsAction = catchAsyncActionError(
  ({ borrowerIds }) =>
    async dispatch => {
      const entries = await Promise.all(
        (borrowerIds || []).map(async borrowerId => [
          borrowerId,
          await resolveBorrowerContactSummary(borrowerId),
        ]),
      );

      return dispatch({
        type: LOAN_APPLICATION_BORROWER_CRM_CONTACTS_ACTION_TYPE.SET_BORROWER_CRM_CONTACTS,
        payload: Object.fromEntries(entries),
      });
    },
);

// Re-fetch a single borrower's contact (used after a link is persisted).
export const refreshBorrowerCrmContactAction = catchAsyncActionError(
  ({ borrowerId }) =>
    async dispatch => {
      const summary = await resolveBorrowerContactSummary(borrowerId);

      return dispatch({
        type: LOAN_APPLICATION_BORROWER_CRM_CONTACTS_ACTION_TYPE.SET_BORROWER_CRM_CONTACT,
        payload: { borrowerId, summary },
      });
    },
);

// A borrower -> contact link/unlink chosen in the borrower edit is held as a
// pending change and only persisted when the borrower's changes are saved.
export const LOAN_APPLICATION_BORROWER_CONTACT_LINK_ACTION_TYPE = createNamedActions(
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/borrowerPendingContactLinks`,
  {
    SET_PENDING_BORROWER_CONTACT_LINK: 'SET_PENDING_BORROWER_CONTACT_LINK',
    SET_PENDING_BORROWER_CONTACT_UNLINK: 'SET_PENDING_BORROWER_CONTACT_UNLINK',
    CLEAR_PENDING_BORROWER_CONTACT_LINK: 'CLEAR_PENDING_BORROWER_CONTACT_LINK',
  },
);

export const setPendingBorrowerContactLink = ({ borrowerId, contact }) => ({
  type: LOAN_APPLICATION_BORROWER_CONTACT_LINK_ACTION_TYPE.SET_PENDING_BORROWER_CONTACT_LINK,
  payload: { borrowerId, contact },
});

export const setPendingBorrowerContactUnlink = ({ borrowerId, contact }) => ({
  type: LOAN_APPLICATION_BORROWER_CONTACT_LINK_ACTION_TYPE.SET_PENDING_BORROWER_CONTACT_UNLINK,
  payload: { borrowerId, contact },
});

export const clearPendingBorrowerContactLink = ({ borrowerId }) => ({
  type: LOAN_APPLICATION_BORROWER_CONTACT_LINK_ACTION_TYPE.CLEAR_PENDING_BORROWER_CONTACT_LINK,
  payload: { borrowerId },
});

// Persist a pending borrower -> contact link/unlink. Called from the borrower
// edit save flow once the borrower itself has been saved.
export const savePendingBorrowerContactLinkAction = catchAsyncActionError(
  ({ borrowerId, applicationId }) =>
    async (dispatch, getState) => {
      const pending = selectBorrowerPendingContactLinkById(getState(), borrowerId);

      const contactId = pending?.contact?.contactId;

      if (!contactId) {
        return undefined;
      }

      const persist =
        pending.action === PENDING_CONTACT_LINK_INTENT.UNLINK
          ? crmContactApi.unlinkContactFromBorrower({ contactId, borrowerId })
          : crmContactApi.linkContactToBorrower({ contactId, borrowerId, applicationId });

      await persist;

      dispatch(clearPendingBorrowerContactLink({ borrowerId }));

      return dispatch(refreshBorrowerCrmContactAction({ borrowerId }));
    },
);
